import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Gift, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-xl bg-[#0B101A] border border-white/8 p-5">
    <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
    <div data-testid={testid} className={`font-display text-3xl mt-1.5 ${accent || "text-white"}`}>{value}</div>
  </div>
);

export const Referrals = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = data.url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!data)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  if (data.error)
    return <p className="text-slate-400 text-sm">Could not load referral data. Try refreshing.</p>;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="referrals-panel">
      <div className="rounded-xl neon-border bg-[#0B101A]/80 p-6">
        <div className="flex items-center gap-3 mb-1.5">
          <Gift className="w-5 h-5 text-cyan-neon" />
          <h2 className="font-display text-xl text-white">Give a shield, get a free month</h2>
        </div>
        <p className="text-sm text-slate-400 mb-5 max-w-2xl">
          Share your invite link. When someone signs up through it and upgrades to a paid plan,
          we credit <span className="text-cyan-neon">one free month</span> of your plan straight to your Stripe balance.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="referral-link-input"
            readOnly
            value={data.url}
            onFocus={(e) => e.target.select()}
            className="flex-1 px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-cyan-neon font-mono text-sm focus:outline-none"
          />
          <button
            data-testid="referral-copy-btn"
            onClick={copyLink}
            className="px-6 py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Sign-ups from your link" value={data.signups} testid="referral-signups" />
        <Stat label="Free months earned" value={data.rewarded + data.pending} testid="referral-earned" accent="text-cyan-neon" />
        <Stat label="Credit earned" value={`$${(data.credit_total_cents / 100).toFixed(0)}`} testid="referral-credit" accent="text-[#39FF14]" />
      </div>

      <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
        <div className="flex items-center gap-2.5 mb-4">
          <Users className="w-4 h-4 text-slate-400" />
          <h3 className="font-display text-white">How it works</h3>
        </div>
        <ol className="space-y-2.5 text-sm text-slate-400 list-none">
          {[
            "Send your invite link to another advertiser.",
            "They sign up and start their free trial — you'll see them counted above.",
            "When they upgrade to any paid plan, one free month of your plan is credited to your next invoice automatically.",
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-5 h-5 shrink-0 rounded-full bg-cyan-neon/15 border border-cyan-neon/30 flex items-center justify-center font-mono text-[10px] text-cyan-neon">{i + 1}</span>
              {step}
            </li>
          ))}
        </ol>
        {data.pending > 0 && (
          <p data-testid="referral-pending-note" className="mt-4 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            You have {data.pending} pending free month{data.pending > 1 ? "s" : ""} — the credit applies automatically once your billing account is active.
          </p>
        )}
      </div>
    </motion.div>
  );
};
